import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Orcamento, OrcamentoDTO, OrcamentoItemDTO } from '../../../../../models/financeiro.model';
import { FinanceiroService } from '../../../../../services/financeiro.service';
import { ErrorModel } from '../../../../../models/error.model';
import { ProdutoModal } from '../../../../modal/produto-modal/produto-modal';
import { ClienteModal } from '../../../../modal/cliente-modal/cliente-modal';
import { ModalStateService } from '../../../../../services/modaState.service';
import { ClienteSimplificado, ProdutoSimplificado, ProdutoSimplificadoPaginado, Servico, Vendedor } from '../../../../../models/cadastro.model';
import { filter, take } from 'rxjs';
import { ServicosModal } from '../../../../modal/servicos-modal/servicos-modal';
import { CadastroService } from '../../../../../services/cadastro.service';
import { ComentarioModalComponent } from '../../../../modal/comentario-grupo-modal/comentario-grupo-modal';
import { ConfirmDialogComponent } from '../../../../modal/confirm-dialog/confirm-dialog';
import { VendaModalV2 } from '../../../../modal/venda-modal-v2/venda-modal-v2';
import { todayLocalDateString, localDateStringPlusDays } from '../../../../../core/utils/date.util';

@Component({
  selector: 'app-orcamento-itens',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ProdutoModal, ClienteModal, ServicosModal, ComentarioModalComponent, ConfirmDialogComponent, VendaModalV2],
  templateUrl: './orcamento-itens.component.html',
  styleUrls: ['./orcamento-itens.component.css']
})
export class OrcamentoItensComponent implements OnInit {
  contador: number = 0;
  orcamento: Orcamento | undefined;
  orcamentoForm: FormGroup;
  itensTemporarios: OrcamentoItemDTO[] = [];
  isEditable: boolean = false;
  isProdutoModalOpen = false;
  isClienteModalOpen = false;
  isServicoModalOpen = false;
  isComentarioModalOpen = false;
  isSomenteVisualizacao = false;
  isConfirmConverterVendaOpen = false;
  isVendaModalOpen = false;
  orcamentoParaVenda: OrcamentoDTO | null = null;
  /** Restrição do cliente atualmente selecionado no formulário — usado para bloquear PARCELADA/VENDA MISTA no venda-modal-v2 (D-11). */
  clienteRestricaoAtiva = false;
  comentarioGrupoAtual: number | null = null;
  comentarioMapaAtual = new Map<string, string>();
  comentariosPorGrupo: Record<number, Map<string, string>> = {};

  valorDescontoAplicado: number = 0;
  valorTotalOrcamento: number = 0;  
  valorSubtotalOrcamento: number = 0;
  valorDescontoOrcamento: number = 0;
  valorAcrescimoOrcamento: number = 0;
  valorCustoInstalacao: number = 0;
  valorDescontoExtra: number = 0;
  valorInstalacoManual: boolean = false;

  vendedor: Vendedor = {
    id: 1,
    nome: '',
    email: '',
    telefone: '',
    funcionario: {
      id: 0,
      cargoId: 0,
      cargoDescricao: '',
      cargoNivel: '',
      nome: '',
      sobrenome: '',
      telefone: '',
      cpf: '',
      cep: '',
      enderecoRua: '',
      enderecoNumeroPredial: '',
      enderecoCidade: '',
      enderecoUf: '',
      enderecoPais: '',
      enderecoBairro: ''
    }
  }

  private idOrcamento: number | null = null;
  private origemTela: string = 'orcamentos';
  private modalStateService = inject(ModalStateService);
  
  constructor(
    private route: ActivatedRoute,
    private financeiroService: FinanceiroService,
    private fb: FormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private cadastroService: CadastroService
  ) {
    const dataHojeStr = todayLocalDateString();
    const dataValidadeStr = localDateStringPlusDays(30);

    this.orcamentoForm = this.fb.group({
      id: [null],
      cliente: this.fb.group({
          id: ['', [Validators.required]],
          nomeRazaoSocial: ['', [Validators.required]]
    }),
      statusOrcamento: ['ABERTO', [Validators.required]],
      dataOrcamento: [dataHojeStr, [Validators.required]],
      validadeOrcamento: [dataValidadeStr, [Validators.required]],
      subtotal: [0],
      valorTotal: [0],
      observacao: [''],
      custoInstalacao: [0],
      descontoAplicado: [0]
    });
  }

  ngOnInit(): void {
    this.origemTela = this.route.snapshot.queryParamMap.get('origem') ?? 'orcamentos';
    this.orcamentoForm.get('statusOrcamento')?.valueChanges.subscribe(() => {
      this.atualizarModoVisualizacao();
    });
    this.atualizarModoVisualizacao();

    this.route.paramMap.subscribe(params => {
      const id = params.get('idOrcamento');
      const idCliente = params.get('idCliente');
      if (id) {
        this.isEditable = true;
        this.idOrcamento = +id;
        this.loadOrcamento();
      } else if (idCliente){
        this.cadastroService.getClienteById(+idCliente).subscribe(cliente => {
            this.orcamentoForm.patchValue({
            cliente: {
              id: cliente.id,
              nomeRazaoSocial: cliente.nomeRazaoSocial
            }
          });
          this.clienteRestricaoAtiva = !!cliente.restricaoAtiva;
        });
      }
    });
  }

  loadOrcamento(): void {
    if (this.idOrcamento) {
      this.financeiroService.getOrcamentoById(this.idOrcamento).subscribe(data => {
        this.orcamento = data;
        this.orcamentoForm.patchValue(data);
        this.clienteRestricaoAtiva = !!data.cliente?.restricaoAtiva;
        this.atualizarModoVisualizacao();
  
        this.loadItensOrcamento(this.orcamento.id);
        
      });
    }
  }

  private atualizarModoVisualizacao(): void {
    const status = (this.orcamentoForm.get('statusOrcamento')?.value ?? '').toString().toUpperCase();
    this.isSomenteVisualizacao = status === 'CONCLUIDO';

    if (this.isSomenteVisualizacao) {
      this.orcamentoForm.disable({ emitEvent: false });
      return;
    }

    this.orcamentoForm.enable({ emitEvent: false });
    this.orcamentoForm.get('id')?.disable({ emitEvent: false });
    this.orcamentoForm.get('cliente.nomeRazaoSocial')?.disable({ emitEvent: false });
  }

   private loadItensOrcamento(id: number): void {
    if (id) {
      this.financeiroService.getItensOrcamentoByIdOrcamento(id).subscribe(data => {
        this.itensTemporarios = data;
        this.calcularTotais();
      }, err => {
        var erro: ErrorModel = err.error;
        this.toastr.error(erro.message);
      });
    }
  }
  
  incluirServico(): void {
    if (this.isSomenteVisualizacao) {
      this.toastr.info('Orçamento concluído permite somente visualização.');
      return;
    }

    this.isServicoModalOpen = true;
    this.modalStateService.modalResult$
      .pipe(take(1))
      .subscribe((servico: Servico | undefined) => {
        this.contador += 1;
        if (servico && this.contador === 1) {
           this.itensTemporarios.push({
                produtoId: 0,
                nomeItem: servico.nome,
                descricaoItem: servico.descricao,
                qtdItem: 1,
                valorItem: servico.precoVenda,
                id: 0,
                grupo: 0,
                orcamentoId: 0,
                servicoId: servico.id,
                subtotal: 0,
                desconto: 0,
                acrescimo: 0,
                total: 0,
                metadata: {}
              });

              let item = this.itensTemporarios[this.itensTemporarios.length - 1];
              this.calcularSubTotal(item);

        } else {
            this.isServicoModalOpen = false;
          }
      });
      this.contador = 0;
  }

  incluirCliente(): void {
    if (this.isSomenteVisualizacao) {
      this.toastr.info('Orçamento concluído permite somente visualização.');
      return;
    }

    this.isClienteModalOpen = true;
    this.modalStateService.modalResult$
      .pipe(take(1))
      .subscribe((cliente: ClienteSimplificado | undefined) => {
        this.contador += 1;
        if (cliente && this.contador === 1) {

          this.orcamentoForm.patchValue({
            cliente: {
              id: cliente.id,
              nomeRazaoSocial: cliente.nomeRazaoSocial
            }
          });
          // ClienteSimplificado não traz restricaoAtiva — busca o cliente completo (D-11).
          this.cadastroService.getClienteById(cliente.id).subscribe(c => {
            this.clienteRestricaoAtiva = !!c.restricaoAtiva;
          });

        }
        this.isClienteModalOpen = false;
      });
      this.contador = 0;
  }

  closeClienteModal(cliente?: any): void {
    this.isClienteModalOpen = false;
  }

  closeServicoModal(servico?: any): void {
    this.isServicoModalOpen = false;
  }

  incluirProduto(): void {
    if (this.isSomenteVisualizacao) {
      this.toastr.info('Orçamento concluído permite somente visualização.');
      return;
    }
  
    this.isProdutoModalOpen = !this.isProdutoModalOpen;

      this.modalStateService.modalResult$
          .pipe(take(1))
          .subscribe((produto: ProdutoSimplificado | undefined) => {
            this.contador += 1;
            if (produto && this.contador === 1) {
              this.itensTemporarios.push({
                produtoId: produto.id,
                nomeItem: produto.nomeProduto,
                descricaoItem: produto.descricao,
                qtdItem: 1,
                valorItem: produto.precoUnitario,
                id: 0,
                grupo: 0,
                orcamentoId: 0,
                servicoId: 0,
                subtotal: 0,
                desconto: 0,
                acrescimo: 0,
                total: 0,
                metadata: {}
              });
              let item = this.itensTemporarios[this.itensTemporarios.length - 1];
              this.calcularSubTotal(item);
          } else {
            this.isProdutoModalOpen = false;
          }
      });
      this.contador = 0;

  }

  closeProdutoModal(produto?: any): void {
    this.isProdutoModalOpen = false;
    this.calcularTotais();
  }

  calcularNovoTotal(item: any): void {
    
    const subtotal = Number(item.subtotal) || 0;
    const acrescimo = Number(item.acrescimo) || 0;
    const desconto = Number(item.desconto) || 0;
  


    const acrescimoCalculado = (acrescimo / 100) * subtotal;
    const descontoCalculado = (desconto / 100) * subtotal;
    const valorTotalCalculado = subtotal + acrescimoCalculado - descontoCalculado;
    
    item.subtotal = subtotal;
    item.total = valorTotalCalculado;

    this.calcularTotais();
    
  }

  calcularSubTotal(item: any): void {
    const precoUnitario = Number(item.valorItem) || 0;
    const quantidade = Number(item.qtdItem) || 0;
    const subtotal = precoUnitario * quantidade;
    item.subtotal = subtotal;
    
    this.calcularNovoTotal(item);
  }

  removerItem(position: number): void {
    if (this.isSomenteVisualizacao) {
      this.toastr.info('Orçamento concluído permite somente visualização.');
      return;
    }

      this.itensTemporarios.splice(position, 1);
      this.calcularTotais();
    
  }

  incluirComentario(position: number): void {
    if (this.isSomenteVisualizacao) {
      this.toastr.info('Orçamento concluído permite somente visualização.');
      return;
    }

    const itemSelecionado = this.itensTemporarios[position];
    if (!itemSelecionado) {
      return;
    }

    const grupo = Number(itemSelecionado.grupo) || 0;
    const metadataMap = this.mapFromMetadata(itemSelecionado.metadata);
    const comentarioExistente = this.cloneComentarioMap(this.comentariosPorGrupo[grupo]);

    const comentarioInicial = metadataMap.size > 0
      ? metadataMap
      : comentarioExistente;

    this.comentarioGrupoAtual = grupo;
    this.comentarioMapaAtual = this.cloneComentarioMap(comentarioInicial);
    this.comentariosPorGrupo[grupo] = this.cloneComentarioMap(comentarioInicial);
    this.isComentarioModalOpen = true;
  }

  fecharComentarioModal(): void {
    this.isComentarioModalOpen = false;
  }

  adicionarComentarioGrupo(comentarioMap: Map<string, string>): void {
    if (this.comentarioGrupoAtual !== null) {
      this.comentariosPorGrupo[this.comentarioGrupoAtual] = this.cloneComentarioMap(comentarioMap);
    } 

    this.fecharComentarioModal();
  }

  hasComentario(item: OrcamentoItemDTO): boolean {
    const grupo = Number(item?.grupo) || 0;
    const comentarioMap = this.comentariosPorGrupo[grupo];

    if (comentarioMap && Array.from(comentarioMap.values()).some((value) => (value ?? '').trim().length > 0)) {
      return true;
    }

    return this.mapFromMetadata(item?.metadata).size > 0;
  }

  private cloneComentarioMap(source?: Map<string, string>): Map<string, string> {
    if (!source) {
      return new Map<string, string>();
    }

    return new Map<string, string>(source);
  }

  private mapFromMetadata(metadata?: Record<string, string> | null): Map<string, string> {
    if (!metadata) {
      return new Map<string, string>();
    }

    const entries = Object.entries(metadata)
      .filter(([key, value]) => key.trim().length > 0 && String(value ?? '').trim().length > 0);

    return new Map<string, string>(entries);
  }

  calcularInstalacao(): void {
    const valorCusto = Number(this.orcamentoForm.get('custoInstalacao')?.value) || 0;
    this.valorCustoInstalacao = valorCusto;
    this.calcularTotais();
  }

  calcularDescontoTotal(): void {
    const descontoExtra = this.parseDecimalValue(this.orcamentoForm.get('descontoAplicado')?.value);
    this.valorDescontoExtra = descontoExtra;
    this.calcularTotais();
  }

  formatarDescontoExtra(): void {
    const descontoExtra = this.parseDecimalValue(this.orcamentoForm.get('descontoAplicado')?.value);
    this.orcamentoForm.get('descontoAplicado')?.setValue(
      descontoExtra.toFixed(2).replace('.', ','),
      { emitEvent: false }
    );
    this.valorDescontoExtra = descontoExtra;
    this.calcularTotais();
  }

  calcularTotais(): void {
    let subtotal = 0;
    let valorTotal = 0;
    let valorDesconto = 0;
    let valorAcrescimo = 0;


    for (const item of this.itensTemporarios) {
      valorTotal += item.total;
      subtotal += item.subtotal;
      valorDesconto +=  item.desconto * item.subtotal / 100;
      valorAcrescimo += item.acrescimo * item.subtotal / 100;
    }

    this.valorSubtotalOrcamento = subtotal;
    this.valorDescontoOrcamento = valorDesconto;
    this.valorAcrescimoOrcamento = valorAcrescimo;
    this.valorCustoInstalacao = Number(this.orcamentoForm.get('custoInstalacao')?.value) || 0;
    this.valorDescontoExtra = this.parseDecimalValue(this.orcamentoForm.get('descontoAplicado')?.value);
    
 
    const totalItens = this.valorSubtotalOrcamento - this.valorDescontoOrcamento + this.valorAcrescimoOrcamento;

    this.valorTotalOrcamento = Math.max(0, totalItens + this.valorCustoInstalacao - this.valorDescontoExtra);

  }

  private parseDecimalValue(rawValue: unknown): number {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return 0;
    }

    if (typeof rawValue === 'number') {
      return Math.max(0, rawValue);
    }

    let normalized = String(rawValue).trim();

    if (normalized.includes(',')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    }

    normalized = normalized.replace(/[^0-9.-]/g, '');
    const parsed = Number(normalized);

    if (!Number.isFinite(parsed)) {
      return 0;
    }

    return Math.max(0, parsed);
  }



  salvar(): void {
    if (this.isSomenteVisualizacao) {
      this.toastr.info('Orçamento concluído permite somente visualização.');
      return;
    }

    this.itensTemporarios.forEach((item) => {
      const comentarioMap = this.comentariosPorGrupo[item.grupo];
      if (!comentarioMap) {
        return;
      }

      const metadataAtual = item.metadata ?? {};
      comentarioMap.forEach((value, key) => {
        metadataAtual[key] = value;
      });
      item.metadata = metadataAtual;
    });

    // Recalcula os totais imediatamente antes de persistir para evitar divergencia
    // entre valor exibido e valor enviado ao backend.
    this.calcularTotais();

    if (this.orcamentoForm.valid && this.orcamento) {
      const updatedOrcamento: Orcamento = {
        ...this.orcamento,
        ...this.orcamentoForm.value,
        itens: this.itensTemporarios
      };

      if (this.idOrcamento) {
        const idOrcamentoAtual = this.idOrcamento;
        updatedOrcamento.valorTotal = this.valorTotalOrcamento;
        updatedOrcamento.subtotal = this.valorSubtotalOrcamento;
        updatedOrcamento.descontoAplicado = this.valorDescontoExtra;
          


        this.financeiroService.updateOrcamento(this.idOrcamento, updatedOrcamento)
        .subscribe(next => {
          this.financeiroService.updateItensOrcamento(this.itensTemporarios, idOrcamentoAtual)
          .subscribe(next => {
              this.toastr.success("Orçamento atualizado com sucesso!");
              this.perguntarConverterEmVenda(idOrcamentoAtual);
          }, err => {
            var erro: ErrorModel = err.error;
            this.toastr.error(erro.message);
          });
        }, err => {
            var erro: ErrorModel = err.error;
            this.toastr.error(erro.message);
        });

      }
    } else {


      

      const novoOrcamento: Orcamento = {
        ...this.orcamento,
        ...this.orcamentoForm.value,
        itens: this.itensTemporarios
      };
      
      novoOrcamento.valorTotal = this.valorTotalOrcamento;
      novoOrcamento.subtotal = this.valorSubtotalOrcamento;
      novoOrcamento.descontoAplicado = this.valorDescontoExtra
      novoOrcamento.vendedor = this.vendedor;

  

      
      this.financeiroService.createOrcamento(novoOrcamento)
      .subscribe(created => {
        this.financeiroService.updateItensOrcamento(this.itensTemporarios, created.id)
          .subscribe(() => {
            const orcamentoConsolidado: Orcamento = {
              ...novoOrcamento,
              id: created.id
            };

            this.financeiroService.updateOrcamento(created.id, orcamentoConsolidado)
              .subscribe(() => {
                this.toastr.success("Orçamento criado com sucesso!");
                this.perguntarConverterEmVenda(created.id);
              }, err => {
                var erro: ErrorModel = err.error;
                this.toastr.error(erro.message);
              });
          }, err => {
            var erro: ErrorModel = err.error;
            this.toastr.error(erro.message);
          });

      }, err => {

        var erro: ErrorModel = err.error;
       this.toastr.error(erro.message);
      }); 


    }
     
      
  }

  cancelar(): void {
    if (this.origemTela === 'vendas') {
      this.router.navigate(['/home', 'venda-venda']);
      return;
    }

    if (this.origemTela === 'homepage') {
      this.router.navigate(['/home', 'homepage']);
      return;
    }

    this.router.navigate(['/home', 'venda-orcamento']);
  }

  private perguntarConverterEmVenda(idOrcamento: number): void {
    this.orcamentoParaVenda = {
      id: idOrcamento,
      idCliente: Number(this.orcamentoForm.get('cliente.id')?.value ?? 0),
      idVendedor: this.vendedor?.id ?? 0,
      nomeCliente: this.orcamentoForm.get('cliente.nomeRazaoSocial')?.value ?? '',
      descontoAplicado: this.valorDescontoExtra,
      valorTotal: this.valorTotalOrcamento,
      dataOrcamento: this.orcamentoForm.get('dataOrcamento')?.value ?? '',
      validadeOrcamento: this.orcamentoForm.get('validadeOrcamento')?.value ?? '',
      diasEmAberto: 0,
      restricaoAtiva: this.clienteRestricaoAtiva
    };
    this.isConfirmConverterVendaOpen = true;
    this.cdr.detectChanges();
  }

  confirmarConverterEmVenda(): void {
    this.isConfirmConverterVendaOpen = false;
    this.isVendaModalOpen = true;
    this.cdr.detectChanges();
  }

  recusarConverterEmVenda(): void {
    this.isConfirmConverterVendaOpen = false;
    this.router.navigate(['/home', 'venda-orcamento']);
  }

  fecharVendaModal(): void {
    this.isVendaModalOpen = false;
    this.router.navigate(['/home', 'venda-orcamento']);
  }
}
function takeUntilDestroyed(): import("rxjs").OperatorFunction<any, unknown> {
  throw new Error('Function not implemented.');
}

